// apps/pdf-suite/web/src/lib/licenseToken.js
// The desktop offline-license token (Phase 2 — logout-capable activation).
//
// Storage: electron-store is canonical ('license' key, via license:* IPC);
// localStorage['scrivar_license'] is a synchronous mirror. DELIBERATELY a
// different key from 'scrivar_session' — auth.signOut clears the session but
// must never touch the license: that separation is the whole feature.
//
// Issuance (ensureLicenseToken) happens only on deliberate user acts —
// interactive sign-in and activation (design decision P2-4) — so two machines
// can't thrash the server-side device registry by merely launching.
import { isDesktop } from './platform';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';
const STORAGE_KEY = 'scrivar_license';

// Launch verification result. checked stays false until verifyStoredLicense
// resolves; the App gate holds rendering on desktop until then.
let state = { checked: false, valid: false, claims: null };

export function getStoredLicenseToken() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return raw?.token || null;
  } catch {
    return null;
  }
}

export function hasStoredLicenseToken() {
  return Boolean(getStoredLicenseToken());
}

function rememberToken(token) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ token })); } catch { /* ignore */ }
  try { window.electronAPI?.storeLicense?.({ token })?.catch?.(() => {}); } catch { /* ignore */ }
}

export async function clearLicense() {
  state = { checked: true, valid: false, claims: null };
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  try { await window.electronAPI?.clearLicense?.(); } catch { /* ignore */ }
}

/**
 * Verify the stored token against this machine — main-process Ed25519 check,
 * fully offline. Restores the localStorage mirror from electron-store if a
 * site-data wipe removed it. Caches the result for the sync accessors.
 */
export async function verifyStoredLicense() {
  if (!isDesktop() || !window.electronAPI?.verifyLicense) {
    state = { checked: true, valid: false, claims: null };
    return state;
  }
  let token = getStoredLicenseToken();
  if (!token && window.electronAPI.getLicense) {
    try {
      const stored = await window.electronAPI.getLicense();
      if (stored?.token) {
        token = stored.token;
        rememberToken(token);
      }
    } catch { /* ignore */ }
  }
  if (!token) {
    state = { checked: true, valid: false, claims: null };
    return state;
  }
  let result = await attemptVerify(token);
  if (!result) {
    // Transient failure at launch (IPC startup race / main-side internal
    // error). Retry once before giving up — for a logged-out licensed device
    // this verify is the only thing between the user and a /login bounce.
    await new Promise((r) => setTimeout(r, 300));
    result = await attemptVerify(token);
  }
  state = result
    ? { checked: true, valid: Boolean(result.valid), claims: result.valid ? result.claims : null }
    : { checked: false, valid: false, claims: null }; // inconclusive — never recorded as "verified invalid"
  return state;
}

// A single verify attempt. null = TRANSIENT failure — the IPC invoke rejected
// or the main handler returned its internal-error shape ({valid:false,
// reason:'error'}). Genuine negatives (bad_signature, device_mismatch,
// expired, malformed, unknown_kid, bad_key) pass through as definitive.
async function attemptVerify(token) {
  try {
    const result = await window.electronAPI.verifyLicense(token);
    if (result && !(result.valid === false && result.reason === 'error')) return result;
  } catch { /* transient IPC failure */ }
  return null;
}

export function licenseChecked() { return state.checked; }
export function hasValidLicense() { return state.checked && state.valid; }
export function getLicenseClaims() { return state.valid ? state.claims : null; }

/**
 * Issue (or refresh) this device's license token. Call ONLY from deliberate
 * user acts: interactive sign-in (auth.js), activation success
 * (bindPendingCode / ActivationModal). The server picks the account's best
 * activated lifetime code and moves the code's license device here.
 * All failures are silent — the next deliberate act retries.
 */
let ensureInFlight = null;
export async function ensureLicenseToken() {
  if (!isDesktop() || !window.electronAPI?.getDeviceFingerprint) return { issued: false };
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = (async () => {
    try {
      const { auth } = await import('./firebase'); // dynamic: avoids an import cycle with auth.js
      const user = auth.currentUser;
      if (!user) return { issued: false };
      const fingerprint = await window.electronAPI.getDeviceFingerprint();
      if (!fingerprint) return { issued: false };
      const idToken = await user.getIdToken();
      const res = await fetch(`${API}/license/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ deviceFingerprint: fingerprint }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.licenseToken) {
        rememberToken(data.licenseToken);
        await verifyStoredLicense();
        return { issued: true };
      }
      if (res.status === 404 && data.reason === 'no_lifetime_code') {
        // The signed-in account owns no lifetime code — a leftover local token
        // is stale (code transferred away / never owned on this account).
        await clearLicense();
      }
      return { issued: false };
    } catch {
      return { issued: false }; // offline / transient — keep whatever we have
    } finally {
      ensureInFlight = null;
    }
  })();
  return ensureInFlight;
}

/**
 * Opportunistic online re-validation (launch cadence). ONLY an HTTP-200
 * verdict with a recognized reason counts against the user — offline, 5xx,
 * and rate-limit responses change nothing (the server_error-is-not-a-strike
 * rule). Genuine negatives clear the token immediately: every negative here
 * is deterministic DB state (revoked / account changed / device superseded),
 * and the acceptance criterion is "revoke re-locks on the next online validate".
 */
const GENUINE_NEGATIVE_REASONS = ['invalid', 'revoked', 'transferred', 'expired'];
export async function validateLicenseOnline() {
  const token = getStoredLicenseToken();
  if (!token) return { checked: false };
  try {
    const res = await fetch(`${API}/license/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return { checked: false };
    const data = await res.json().catch(() => null);
    if (!data) return { checked: false };
    if (data.valid) return { checked: true, valid: true, tier: data.tier };
    if (GENUINE_NEGATIVE_REASONS.includes(data.reason)) {
      await clearLicense();
      return { checked: true, valid: false, reason: data.reason };
    }
    return { checked: false };
  } catch {
    return { checked: false };
  }
}
